/**
 * UserGuide — trang "Hướng dẫn sử dụng", nhúng lại nội dung từ
 * huong-dan-su-dung-3-vai-tro.html (screenshot Playwright 3 vai trò), tách
 * theo file riêng dưới public/guides/ và lọc theo role user đang đăng nhập:
 * - superadmin/tenant_admin: xem cả 3 (Sinh viên, Giảng viên, Tenant Admin)
 * - instructor: xem Giảng viên + Sinh viên
 * - student: chỉ xem Sinh viên
 */
import { useEffect, useState } from "react";
import { Alert, Spin } from "antd";
import { useAuthStore } from "@/stores/auth";

type GuideRole = "student" | "instructor" | "tenant_admin";

const GUIDE_LABEL: Record<GuideRole, string> = {
  student: "Sinh viên",
  instructor: "Giảng viên",
  tenant_admin: "Tenant Admin",
};

function guidesForRole(role: string | undefined): GuideRole[] {
  if (role === "tenant_admin" || role === "superadmin") {
    return ["student", "instructor", "tenant_admin"];
  }
  if (role === "instructor") {
    return ["student", "instructor"];
  }
  return ["student"];
}

export function UserGuide() {
  const role = useAuthStore((s) => s.user?.role);
  const visibleRoles = guidesForRole(role);
  const [sections, setSections] = useState<Partial<Record<GuideRole, string>> | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/guides/guide.css";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSections(null);
    setError(false);
    Promise.all(
      visibleRoles.map((r) =>
        fetch(`/guides/role-${r}.html`).then((res) => {
          if (!res.ok) throw new Error(`fetch role-${r} failed`);
          return res.text();
        }),
      ),
    )
      .then((htmls) => {
        if (cancelled) return;
        const map: Partial<Record<GuideRole, string>> = {};
        visibleRoles.forEach((r, i) => {
          map[r] = htmls[i];
        });
        setSections(map);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRoles.join(",")]);

  if (error) {
    return <Alert type="error" showIcon message="Không tải được nội dung hướng dẫn." />;
  }

  if (!sections) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="guide-doc">
      <h1>Hướng dẫn sử dụng — F-Future E-learning Platform 2026</h1>
      {visibleRoles.length > 1 && (
        <nav className="toc">
          {visibleRoles.map((r) => (
            <a key={r} href={`#role-${r}`}>
              {GUIDE_LABEL[r]}
            </a>
          ))}
        </nav>
      )}
      {visibleRoles.map((r) => (
        <div key={r} dangerouslySetInnerHTML={{ __html: sections[r] ?? "" }} />
      ))}
      <footer>
        F-Future E-learning Platform 2026 — hướng dẫn tạo tự động bằng Playwright, chụp trên local
        dev.
      </footer>
    </div>
  );
}

export default UserGuide;
